import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Mail, 
  Phone, 
  Clock, 
  Eye, 
  Trash2, 
  Loader2,
  MessageSquare,
  CheckCircle2,
  Inbox
} from 'lucide-react';
import { getMessages, markMessageRead, deleteMessage } from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchMessages = async () => {
    try {
      const response = await getMessages();
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Mesajlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleView = async (message) => {
    setSelectedMessage(message);
    setViewDialogOpen(true);
    
    if (!message.is_read) {
      try {
        await markMessageRead(message.id);
        setMessages(prev => 
          prev.map(m => m.id === message.id ? { ...m, is_read: true } : m)
        );
      } catch (error) {
        console.error('Error marking message as read:', error);
      }
    }
  };

  const handleDelete = (message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedMessage) return;
    
    try {
      await deleteMessage(selectedMessage.id);
      toast.success('Mesaj başarıyla silindi');
      setMessages(prev => prev.filter(m => m.id !== selectedMessage.id));
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Mesaj silinirken hata oluştu');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedMessage(null);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: tr });
    } catch {
      return dateString;
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-messages">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mesajlar
            </h2>
            <p className="text-slate-600">
              İletişim formundan gelen mesajları yönetin
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-blue-500">{unreadCount} yeni</Badge>
              )}
            </p>
          </div>
        </div>

        {/* Messages List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
          </div>
        ) : messages.length > 0 ? (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <Card 
                key={message.id}
                data-testid={`message-item-${index}`}
                className={`border-slate-200 transition-colors ${
                  !message.is_read ? 'bg-blue-50/50 border-blue-200' : ''
                }`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-800">
                          {message.name}
                        </h3>
                        {!message.is_read && (
                          <Badge className="bg-blue-500">Yeni</Badge>
                        )}
                      </div>
                      
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        {message.subject}
                      </p>
                      
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">
                        {message.message}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {message.email}
                        </span>
                        {message.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {message.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(message.created_at)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`view-message-${index}`}
                        onClick={() => handleView(message)}
                        className="gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        Görüntüle
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`delete-message-${index}`}
                        onClick={() => handleDelete(message)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="py-20 text-center">
              <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">
                Henüz mesaj yok
              </h3>
              <p className="text-slate-600">
                İletişim formundan gelen mesajlar burada görüntülenecek
              </p>
            </CardContent>
          </Card>
        )}

        {/* View Message Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="view-message-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#005CB2]" />
                Mesaj Detayı
              </DialogTitle>
            </DialogHeader>
            {selectedMessage && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800 text-lg">
                      {selectedMessage.name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {formatDate(selectedMessage.created_at)}
                    </p>
                  </div>
                  {selectedMessage.is_read && (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Okundu
                    </Badge>
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${selectedMessage.email}`} className="text-[#005CB2] hover:underline">
                      {selectedMessage.email}
                    </a>
                  </p>
                  {selectedMessage.phone && (
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${selectedMessage.phone}`} className="text-[#005CB2] hover:underline">
                        {selectedMessage.phone}
                      </a>
                    </p>
                  )}
                </div>
                
                <div className="pt-4 border-t border-slate-200">
                  <p className="font-medium text-slate-800 mb-2">
                    {selectedMessage.subject}
                  </p>
                  <p className="text-slate-600 whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    className="flex-1 bg-[#005CB2] hover:bg-[#004B92]"
                    onClick={() => window.location.href = `mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Yanıtla
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setViewDialogOpen(false)}
                  >
                    Kapat
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-message-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Mesajı Sil</AlertDialogTitle>
              <AlertDialogDescription>
                Bu mesajı silmek istediğinizden emin misiniz? 
                Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                data-testid="confirm-delete-message-btn"
                className="bg-red-600 hover:bg-red-700"
              >
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
